/**
 * focusActions - Write atoms for entering/exiting focus mode and managing snapshots.
 *
 * These are Jotai write atoms: `atom(null, async (get, set, payload) => ...)`
 * They encapsulate multi-atom mutations that need to happen atomically.
 */

import { atom, type WritableAtom } from 'jotai';

import {
	ExploredNodeArisAtom,
	FocusSnapshotsAtom,
	InflightDiscoveryAtom,
	type FocusSnapshot,
} from '../atoms/cacheAtoms';
import {
	DiscoveredOutgoingAtom,
	DiscoveredIncomingAtom,
	DiscoveryStatusAtom,
	ExplorationErrorAtom,
	LoadingRelKeysAtom,
	DiscoveryFailuresAtom,
} from '../atoms/explorationAtoms';
import {
	FocusedNodeAtom,
	ActiveNodeAtom,
	NavigationPathAtom,
	SelectedRelationshipsAtom,
	FocusDirectionAtom,
} from '../atoms/focusAtoms';
import type { ObjectNodeData } from '../nodes/ObjectNode';

import { DiscoverForNodeAtom } from './explorationActions';

// ============================================================================
// Enter focus mode
// ============================================================================

/**
 * Enter focus mode on a base node. Only base nodes (from the original query)
 * can be focused.
 *
 * If the node was previously focused, the explored graph (selected
 * relationships / loaded nodes) is preserved so the user doesn't lose
 * their work. However, the active node is always reset back to the
 * focused node and breadcrumbs are cleared — this ensures the panel
 * shows the focused node's relationships, not whatever drill position
 * the user happened to be at when they last exited.
 *
 * Usage: `const enterFocus = useSetAtom(EnterFocusModeAtom); enterFocus(nodeData);`
 */
export const EnterFocusModeAtom: WritableAtom<null, [nodeData: ObjectNodeData], Promise<void>> & {
	init: null;
} = atom(null, async (get, set, nodeData: ObjectNodeData) => {
	set(FocusedNodeAtom, nodeData);

	// Check for a saved snapshot for this node
	const snapshots = get(FocusSnapshotsAtom);
	const snapshot = snapshots.get(nodeData.id);

	if (snapshot) {
		// Restore the explored graph (selected relationships) so loaded
		// nodes remain on the canvas, but reset the active node back to
		// the focused node and clear breadcrumbs.
		set(SelectedRelationshipsAtom, snapshot.selectedRelationships);
		set(NavigationPathAtom, []);
		set(ActiveNodeAtom, null);

		// Discover relationships for the focused node (not the previously
		// active drilled-into node)
		await set(DiscoverForNodeAtom, nodeData);
	} else {
		// Fresh focus — no previous state
		set(ActiveNodeAtom, null);
		set(NavigationPathAtom, []);
		set(SelectedRelationshipsAtom, []);
		await set(DiscoverForNodeAtom, nodeData);
	}
});

// ============================================================================
// Exit focus mode
// ============================================================================

/**
 * Exit focus mode entirely. Saves a snapshot of the current focus state
 * so it can be restored if the user re-focuses the same node.
 *
 * Usage: `const exitFocus = useSetAtom(ExitFocusModeAtom); exitFocus();`
 */
export const ExitFocusModeAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	const focusedNode = _get(FocusedNodeAtom);

	// Save snapshot before exiting
	if (focusedNode) {
		const snapshot: FocusSnapshot = {
			selectedRelationships: _get(SelectedRelationshipsAtom),
			navigationPath: _get(NavigationPathAtom),
			activeNode: _get(ActiveNodeAtom),
		};

		set(FocusSnapshotsAtom, (prev) => {
			const next = new Map(prev);
			next.set(focusedNode.id, snapshot);
			return next;
		});
	}

	// Clear focus state
	set(FocusedNodeAtom, null);
	set(ActiveNodeAtom, null);
	set(NavigationPathAtom, []);
	set(SelectedRelationshipsAtom, []);
	set(FocusDirectionAtom, 'outgoing');
	set(DiscoveredOutgoingAtom, []);
	set(DiscoveredIncomingAtom, []);
	set(DiscoveryStatusAtom, 'idle');
	set(ExplorationErrorAtom, null);
	set(DiscoveryFailuresAtom, { outgoing: null, incoming: null });
	set(LoadingRelKeysAtom, new Set());
});

// ============================================================================
// Reset all exploration state (used when new query results arrive)
// ============================================================================

/**
 * Resets all exploration and focus state. Called when new base query results
 * arrive to ensure a clean slate.
 *
 * Usage: `const reset = useSetAtom(ResetExplorationAtom); reset();`
 */
export const ResetExplorationAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	set(FocusedNodeAtom, null);
	set(ActiveNodeAtom, null);
	set(NavigationPathAtom, []);
	set(SelectedRelationshipsAtom, []);
	set(FocusDirectionAtom, 'outgoing');
	set(DiscoveredOutgoingAtom, []);
	set(DiscoveredIncomingAtom, []);
	set(DiscoveryStatusAtom, 'idle');
	set(ExplorationErrorAtom, null);
	set(DiscoveryFailuresAtom, { outgoing: null, incoming: null });
	set(LoadingRelKeysAtom, new Set());
	set(ExploredNodeArisAtom, new Set());
	set(InflightDiscoveryAtom, new Map());
});
